import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../../../../../core/auth';
import { useAppDispatch, useAppSelector } from '../../../../../../lib/store';
import { selectBatchId } from '../../../../../supervisor-queue/state/selector';
import { useGetSupervisorWorkspaceQuery } from '../../../../api/supervisorApiSlice';
import { useSupervisorMode } from '../../../../context/SupervisorModeContext';
import { setResolvedAllConceptsExpanded } from '../../../../state/supervisorSlice';
import { ResolvedConceptAnnotations } from '../../../../types/types';

interface UseResolvedConceptCardResults {
  resolvedConceptCards: ResolvedConceptAnnotations[];
  resolvedDataLoading: boolean;
  setAllConceptsExpanded: () => void;
  clearAllConceptsExpanded: () => void;
}

export const useResolvedTab = (): UseResolvedConceptCardResults => {
  const { encounterId } = useParams();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  const batchId = useAppSelector(selectBatchId);
  const {
    data: supervisorWorkspace,
    isLoading: isSupervisorWorkspaceLoading,
    isFetching: isSupervisorWorkspaceFetching,
  } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );
  const { formatMessage } = useIntl();

  const resolvedConceptCards =
    supervisorWorkspace?.data.annotationsReview
      .map((item) => {
        const resolvedFromConflicts = item.conflicts.flatMap((conflict) => {
          const resolvedAnnotations = conflict.resolved?.annotations;
          if (!resolvedAnnotations?.length) return [];

          return resolvedAnnotations.map((resolved) => {
            let annotatorName = 'Supervisor';

            if (conflict.annotator1Annotation?.annotationId === resolved.annotationId) {
              annotatorName = formatMessage({ id: 'supervisorReview.resolvedTab.annotator1' });
            } else if (conflict.annotator2Annotation?.annotationId === resolved.annotationId) {
              annotatorName = formatMessage({ id: 'supervisorReview.resolvedTab.annotator2' });
            }

            return {
              ...resolved,
              annotatorName,
              isResolvedFromConflict: true,
            };
          });
        });

        const matchingAnnotations = item.matchingAnnotation.map((annotation) => ({
          ...annotation,
          annotatorName: formatMessage({ id: 'supervisorReview.resolvedTab.accepted' }),
          isResolvedFromConflict: false,
        }));

        const supervisorAnnotations = (item.supervisorAnnotations ?? []).map((entry) => ({
          ...entry.annotation,
          annotatorName: 'Supervisor',
          isResolvedFromConflict: true,
        }));

        const annotations = [
          ...matchingAnnotations,
          ...resolvedFromConflicts,
          ...supervisorAnnotations,
        ];

        return {
          conceptId: item.conceptId,
          conceptName: item.conceptName,
          annotations,
        };
      })
      .filter((item) => item.annotations.length > 0) ?? [];
  const setAllConceptsExpanded = () => dispatch(setResolvedAllConceptsExpanded(true));
  const clearAllConceptsExpanded = () => dispatch(setResolvedAllConceptsExpanded(false));

  return {
    resolvedConceptCards,
    resolvedDataLoading: isSupervisorWorkspaceFetching || isSupervisorWorkspaceLoading,
    setAllConceptsExpanded,
    clearAllConceptsExpanded,
  };
};
